import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { newsStore, type NewsItem } from "../utils/dataStore";
import { apiPost, apiPut } from "../../../api/http";
import { uploadTenantImageFile } from "../../../api/tenantImageUpload";
import { uploadTenantVideoFile } from "../../../api/tenantVideoUpload";
import { getStoredWxOpenIdForTenant } from "../../../api/wxOpenIdStorage";
import {
  localDateTimeToScheduledIso,
  mapNews,
  scheduledIsoToLocalDateTime,
} from "../../../api/loadDataStore";
import { reportError, showAlert } from "../../../api/errors";
import ImageLibraryPicker from "../../../components/feature/ImageLibraryPicker";

const categories = ["协会公告", "赛事预告", "协会新闻", "会员福利", "平台公告", "球迷故事"];

interface InsertImageForm {
  url: string;
  alt: string;
  width: string;
}

interface InsertVideoForm {
  url: string;
}

/** 正文字数（剔除 HTML 与空白字符），用于底部统计。 */
function stripHtmlWordCount(html: string): number {
  const d = document.createElement("div");
  d.innerHTML = html;
  return (d.textContent || "").replace(/\s/g, "").length;
}

/** 从正文 HTML 中解析视频直链与嵌入 iframe，供编辑页展示「域名 + 完整地址」。 */
function extractBodyVideoRefsFromHtml(html: string): { src: string; domain: string }[] {
  if (typeof document === "undefined" || !html.trim()) return [];
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return [];
  }
  const seen = new Set<string>();
  const out: { src: string; domain: string }[] = [];

  const push = (raw: string | null | undefined) => {
    const src = (raw ?? "").trim();
    if (!src || seen.has(src)) return;
    seen.add(src);
    let domain = "—";
    if (src.startsWith("blob:")) {
      domain = "本地临时（blob，未保存到 OSS）";
    } else if (/^https?:\/\//i.test(src)) {
      try {
        domain = new URL(src).hostname || "—";
      } catch {
        domain = "无法解析的地址";
      }
    } else {
      domain = "非 HTTP(S) 链接";
    }
    out.push({ src, domain });
  };

  doc.querySelectorAll("video[src]").forEach((el) => push(el.getAttribute("src")));
  doc.querySelectorAll("video source[src]").forEach((el) => push(el.getAttribute("src")));
  doc.querySelectorAll("iframe[src]").forEach((el) => push(el.getAttribute("src")));
  return out;
}

/** 正文内视频/嵌入块外层（含历史稿件仅有 `my-3` 的节点）。 */
function findVideoEmbedWrap(root: HTMLElement, target: HTMLElement): HTMLElement | null {
  const marked = target.closest("[data-rich-video-wrap]");
  if (marked && root.contains(marked)) return marked as HTMLElement;
  const legacy = target.closest(".my-3");
  if (legacy && root.contains(legacy) && legacy.querySelector("video, iframe")) {
    return legacy as HTMLElement;
  }
  return null;
}

export function RichTextEditor({
  onChange,
  initialValue,
  placeholder,
  storageTenantLegacyId,
  fillHeight = false,
}: {
  onChange: (html: string) => void;
  initialValue?: string;
  placeholder?: string;
  /** 正文「本地上传」仅走租户对象存储，与运维策略一致。 */
  storageTenantLegacyId: string;
  /** 为 true 时占满父级剩余高度，编辑区内部滚动（需父级为 flex 且给足高度）。 */
  fillHeight?: boolean;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(!initialValue);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [imgTab, setImgTab] = useState<"url" | "upload">("url");
  const [videoTab, setVideoTab] = useState<"url" | "upload">("url");
  const [imgForm, setImgForm] = useState<InsertImageForm>({ url: "", alt: "", width: "100%" });
  const [videoForm, setVideoForm] = useState<InsertVideoForm>({ url: "" });
  const [linkUrl, setLinkUrl] = useState("");
  const savedRange = useRef<Range | null>(null);
  const imgFileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);

  // ── Image select / resize state ──
  const [selectedImgEl, setSelectedImgEl] = useState<HTMLImageElement | null>(null);
  const [imgOverlay, setImgOverlay] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [selectedVideoWrapEl, setSelectedVideoWrapEl] = useState<HTMLElement | null>(null);
  const [videoOverlay, setVideoOverlay] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [wordCount, setWordCount] = useState(() => stripHtmlWordCount(initialValue ?? ""));

  useEffect(() => {
    if (!editorRef.current) return;
    if (initialValue) {
      editorRef.current.innerHTML = initialValue;
      setIsEmpty(false);
      setWordCount(stripHtmlWordCount(initialValue));
    } else {
      editorRef.current.innerHTML = "";
      setIsEmpty(true);
      setWordCount(0);
    }
  }, [initialValue]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    if (savedRange.current) {
      const sel = window.getSelection();
      if (sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    }
  };

  const execCmd = useCallback((cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value ?? undefined);
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(!html || html === "<br>" || html === "");
    setWordCount(stripHtmlWordCount(html));
  }, [onChange]);

  const handleInput = () => {
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(!html || html === "<br>" || html === "");
    setWordCount(stripHtmlWordCount(html));
  };

  // ── Update overlay position ──
  const updateImgOverlay = useCallback((img: HTMLImageElement) => {
    if (!editorWrapperRef.current) return;
    const wrapperRect = editorWrapperRef.current.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    setImgOverlay({
      top: imgRect.top - wrapperRect.top,
      left: imgRect.left - wrapperRect.left,
      width: imgRect.width,
      height: imgRect.height,
    });
  }, []);

  const updateVideoOverlay = useCallback((wrap: HTMLElement) => {
    if (!editorWrapperRef.current) return;
    const wrapperRect = editorWrapperRef.current.getBoundingClientRect();
    const r = wrap.getBoundingClientRect();
    setVideoOverlay({
      top: r.top - wrapperRect.top,
      left: r.left - wrapperRect.left,
      width: r.width,
      height: r.height,
    });
  }, []);

  const clearVideoSelectionStyle = () => {
    if (selectedVideoWrapEl) {
      selectedVideoWrapEl.style.outline = "";
      selectedVideoWrapEl.style.outlineOffset = "";
    }
  };

  // ── Click on editor to select image / 视频块 ──
  const handleEditorClick = (e: React.MouseEvent) => {
    if (!editorRef.current) return;
    const target = e.target as HTMLElement;

    const videoWrap = findVideoEmbedWrap(editorRef.current, target);
    if (videoWrap) {
      if (selectedImgEl) {
        selectedImgEl.style.outline = "";
        selectedImgEl.style.outlineOffset = "";
        setSelectedImgEl(null);
        setImgOverlay(null);
      }
      if (selectedVideoWrapEl && selectedVideoWrapEl !== videoWrap) clearVideoSelectionStyle();
      videoWrap.style.outline = "2px solid #ef4444";
      videoWrap.style.outlineOffset = "2px";
      setSelectedVideoWrapEl(videoWrap);
      updateVideoOverlay(videoWrap);
      return;
    }

    clearVideoSelectionStyle();
    setSelectedVideoWrapEl(null);
    setVideoOverlay(null);

    if (target.tagName === "IMG") {
      const img = target as HTMLImageElement;
      if (selectedImgEl && selectedImgEl !== img) {
        selectedImgEl.style.outline = "";
        selectedImgEl.style.outlineOffset = "";
      }
      img.style.outline = "2px solid #ef4444";
      img.style.outlineOffset = "2px";
      setSelectedImgEl(img);
      updateImgOverlay(img);
    } else {
      if (selectedImgEl) {
        selectedImgEl.style.outline = "";
        selectedImgEl.style.outlineOffset = "";
      }
      setSelectedImgEl(null);
      setImgOverlay(null);
    }
  };

  // ── Set image width preset ──
  const setImgWidth = (widthVal: string) => {
    if (!selectedImgEl) return;
    if (widthVal === "100%") {
      selectedImgEl.style.width = "";
      selectedImgEl.style.maxWidth = "100%";
    } else {
      selectedImgEl.style.width = widthVal;
      selectedImgEl.style.maxWidth = "100%";
    }
    const h = editorRef.current?.innerHTML ?? "";
    onChange(h);
    setWordCount(stripHtmlWordCount(h));
    // Re-measure after layout
    requestAnimationFrame(() => {
      if (selectedImgEl) updateImgOverlay(selectedImgEl);
    });
  };

  // ── Delete selected image ──
  const deleteSelectedImg = () => {
    if (!selectedImgEl) return;
    selectedImgEl.remove();
    setSelectedImgEl(null);
    setImgOverlay(null);
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(!html || html === "<br>" || html === "");
    setWordCount(stripHtmlWordCount(html));
  };

  const deleteSelectedVideoWrap = () => {
    if (!selectedVideoWrapEl) return;
    selectedVideoWrapEl.querySelectorAll("video[src^='blob:']").forEach((node) => {
      try {
        URL.revokeObjectURL((node as HTMLVideoElement).src);
      } catch {
        /* ignore */
      }
    });
    selectedVideoWrapEl.remove();
    clearVideoSelectionStyle();
    setSelectedVideoWrapEl(null);
    setVideoOverlay(null);
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(!html || html === "<br>" || html === "");
    setWordCount(stripHtmlWordCount(html));
  };

  // ── Resize by dragging bottom-right handle ──
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImgEl) return;
    const startX = e.clientX;
    const startWidth = selectedImgEl.getBoundingClientRect().width;

    const onMouseMove = (ev: MouseEvent) => {
      const newWidth = Math.max(60, startWidth + (ev.clientX - startX));
      selectedImgEl.style.width = `${Math.round(newWidth)}px`;
      selectedImgEl.style.maxWidth = "100%";
      updateImgOverlay(selectedImgEl);
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      const h = editorRef.current?.innerHTML ?? "";
      onChange(h);
      setWordCount(stripHtmlWordCount(h));
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // ── Deselect image on editor scroll ──
  const handleEditorScroll = () => {
    if (selectedImgEl) updateImgOverlay(selectedImgEl);
    if (selectedVideoWrapEl) updateVideoOverlay(selectedVideoWrapEl);
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Backspace" || e.key === "Delete") && selectedVideoWrapEl) {
      e.preventDefault();
      deleteSelectedVideoWrap();
    }
  };

  const handleInsertImage = () => {
    if (!imgForm.url.trim()) return;
    restoreSelection();
    const widthStyle = imgForm.width ? ` style="max-width:${imgForm.width}; width:100%;"` : ' style="max-width:100%;"';
    const alt = imgForm.alt || "图片";
    // 媒体后追加空段落，便于光标落在图片/视频后继续输入
    const htmlToInsert = `<img src="${imgForm.url}" alt="${alt}"${widthStyle} class="rounded-lg my-2" /><p><br></p>`;
    document.execCommand("insertHTML", false, htmlToInsert);
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(false);
    setWordCount(stripHtmlWordCount(html));
    setShowImageDialog(false);
    setImgForm({ url: "", alt: "", width: "100%" });
    setImgTab("url");
  };

  const handleLocalImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tid = storageTenantLegacyId.trim();
    if (!tid) {
      showAlert("无法上传图片：缺少租户标识。");
      e.target.value = "";
      return;
    }
    try {
      const url = await uploadTenantImageFile(file, tid);
      setImgForm((p) => ({ ...p, url }));
    } catch (err) {
      reportError(err, "图片上传失败");
    }
    e.target.value = "";
  };

  const handleLocalVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tid = storageTenantLegacyId.trim();
    if (!tid) {
      showAlert("无法上传视频：缺少租户标识。");
      e.target.value = "";
      return;
    }
    setVideoUploading(true);
    try {
      const url = await uploadTenantVideoFile(file, tid);
      setVideoForm({ url });
    } catch (err) {
      reportError(err, "视频上传失败");
      setVideoForm({ url: "" });
    } finally {
      setVideoUploading(false);
    }
    e.target.value = "";
  };

  const handleInsertVideo = () => {
    if (!videoForm.url.trim()) return;
    restoreSelection();
    let embedHtml = "";
    const url = videoForm.url.trim();
    if (url.includes("youtube.com/watch") || url.includes("youtu.be/")) {
      let videoId = "";
      if (url.includes("v=")) videoId = url.split("v=")[1].split("&")[0];
      else if (url.includes("youtu.be/")) videoId = url.split("youtu.be/")[1].split("?")[0];
      if (videoId) embedHtml = `<div class="my-3 rounded-xl overflow-hidden" data-rich-video-wrap="true" style="position:relative;padding-bottom:56.25%;height:0;"><iframe src="https://www.youtube.com/embed/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;" frameborder="0" allowfullscreen></iframe></div>`;
    } else if (url.includes("bilibili.com")) {
      const bvMatch = url.match(/BV[\w]+/);
      if (bvMatch) embedHtml = `<div class="my-3 rounded-xl overflow-hidden" data-rich-video-wrap="true" style="position:relative;padding-bottom:56.25%;height:0;"><iframe src="https://player.bilibili.com/player.html?bvid=${bvMatch[0]}&page=1" style="position:absolute;top:0;left:0;width:100%;height:100%;" scrolling="no" frameborder="0" allowfullscreen></iframe></div>`;
    }
    if (!embedHtml) {
      embedHtml = `<div class="my-3" data-rich-video-wrap="true"><video src="${url}" controls style="max-width:100%;width:100%;border-radius:8px;"></video></div>`;
    }
    document.execCommand("insertHTML", false, `${embedHtml}<p><br></p>`);
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setIsEmpty(false);
    setWordCount(stripHtmlWordCount(html));
    setShowVideoDialog(false);
    setVideoForm({ url: "" });
    setVideoTab("url");
    setVideoUploading(false);
  };

  const handleInsertLink = () => {
    if (!linkUrl.trim()) return;
    restoreSelection();
    document.execCommand("createLink", false, linkUrl.trim());
    if (editorRef.current) {
      const links = editorRef.current.querySelectorAll("a:not([target])");
      links.forEach((a) => { (a as HTMLAnchorElement).target = "_blank"; });
    }
    const html = editorRef.current?.innerHTML ?? "";
    onChange(html);
    setWordCount(stripHtmlWordCount(html));
    setShowLinkDialog(false);
    setLinkUrl("");
  };

  const toolbarButtons: { icon: string; label: string; cmd?: string; cmdValue?: string; action?: string; isMedia?: boolean }[] = [
    { icon: "ri-arrow-go-back-line", label: "撤销", cmd: "undo" },
    { icon: "ri-arrow-go-forward-line", label: "重做", cmd: "redo" },
    { icon: "ri-bold", label: "加粗", cmd: "bold" },
    { icon: "ri-italic", label: "斜体", cmd: "italic" },
    { icon: "ri-underline", label: "下划线", cmd: "underline" },
    { icon: "ri-h-1", label: "小标题", cmd: "formatBlock", cmdValue: "h2" },
    { icon: "ri-h-2", label: "副标题", cmd: "formatBlock", cmdValue: "h3" },
    { icon: "ri-double-quotes-l", label: "引用", cmd: "formatBlock", cmdValue: "blockquote" },
    { icon: "ri-list-unordered", label: "无序列表", cmd: "insertUnorderedList" },
    { icon: "ri-list-ordered", label: "有序列表", cmd: "insertOrderedList" },
    { icon: "ri-separator", label: "分割线", cmd: "insertHorizontalRule" },
    { icon: "ri-align-left", label: "左对齐", cmd: "justifyLeft" },
    { icon: "ri-align-center", label: "居中", cmd: "justifyCenter" },
    { icon: "ri-link", label: "插入链接", action: "link", isMedia: true },
    { icon: "ri-image-line", label: "图片", action: "image", isMedia: true },
    { icon: "ri-movie-line", label: "视频", action: "video", isMedia: true },
  ];

  const handleToolbarClick = (btn: typeof toolbarButtons[0]) => {
    if (btn.action === "image") { saveSelection(); setShowImageDialog(true); }
    else if (btn.action === "video") {
      saveSelection();
      setVideoUploading(false);
      setShowVideoDialog(true);
    }
    else if (btn.action === "link") { saveSelection(); setShowLinkDialog(true); }
    else if (btn.cmd) execCmd(btn.cmd, btn.cmdValue);
  };

  useEffect(() => {
    if (!fillHeight && editorRef.current) {
      editorRef.current.style.minHeight = "min(520px, 55vh)";
    }
  }, [fillHeight]);

  return (
    <div
      className={`overflow-hidden rounded-lg border border-gray-200/80 bg-white shadow-sm ${
        fillHeight ? "flex min-h-0 flex-1 flex-col" : ""
      }`}
    >
      <style>{`
        [data-toutiao-editor] { font-family: system-ui, -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif; }
        [data-toutiao-editor] h2 { font-size: 1.25rem; font-weight: 700; color: #1a1a1a; margin: 1.35em 0 0.5em; line-height: 1.35; }
        [data-toutiao-editor] h3 { font-size: 1.1rem; font-weight: 600; color: #262626; margin: 1.1em 0 0.4em; line-height: 1.4; }
        [data-toutiao-editor] p { margin: 0.65em 0; }
        [data-toutiao-editor] blockquote {
          margin: 1em 0; padding: 0.5em 0 0.5em 1em; border-left: 4px solid #f04142;
          background: #fafafa; color: #595959; font-size: 0.95em;
        }
        [data-toutiao-editor] ul, [data-toutiao-editor] ol { margin: 0.65em 0; padding-left: 1.35em; }
        [data-toutiao-editor] li { margin: 0.25em 0; }
        [data-toutiao-editor] a { color: #f04142; text-decoration: underline; }
        [data-toutiao-editor] hr { border: none; border-top: 1px solid #e8e8e8; margin: 1.5em 0; }
        [data-toutiao-editor] img { max-width: 100%; height: auto; border-radius: 6px; }
        /* 编辑区内点击穿透到外层，便于选中整段嵌入并删除（侧栏手机预览仍可交互） */
        [data-toutiao-editor] [data-rich-video-wrap] iframe,
        [data-toutiao-editor] [data-rich-video-wrap] video,
        [data-toutiao-editor] .my-3 > iframe,
        [data-toutiao-editor] .my-3 > video {
          pointer-events: none;
        }
      `}</style>
      {/* 参考头条号：顶栏工具条 + 沉浸式正文区 */}
      <div className="sticky top-0 z-30 flex flex-shrink-0 flex-nowrap items-center gap-0.5 overflow-x-auto border-b border-gray-100 bg-white/95 px-2 py-1.5 backdrop-blur-sm">
        {toolbarButtons.map((btn, i) => {
          const isDivider = [2, 8, 13].includes(i);
          return (
            <span key={`${btn.label}-${i}`} className="flex flex-shrink-0 items-center">
              {isDivider && <span className="mx-1 h-5 w-px flex-shrink-0 bg-gray-200" />}
              <button
                type="button"
                title={btn.label}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleToolbarClick(btn);
                }}
                className={`flex h-9 min-w-[36px] flex-shrink-0 items-center justify-center rounded-md transition-colors cursor-pointer ${
                  btn.isMedia ? "text-[#f04142] hover:bg-red-50" : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <i className={`${btn.icon} text-lg`} />
              </button>
            </span>
          );
        })}
      </div>

      {/* Editor wrapper with relative positioning for overlay */}
      <div
        ref={editorWrapperRef}
        className={`relative bg-[#fafafa] ${fillHeight ? "min-h-0 flex-1 overflow-y-auto" : ""}`}
        onScroll={handleEditorScroll}
      >
        {isEmpty && (
          <p className="pointer-events-none absolute left-8 top-8 z-10 select-none text-[17px] leading-[1.8] text-gray-300 sm:left-12">
            {placeholder ?? "请输入正文，清晰表达更有助于传播…"}
          </p>
        )}
        <div
          ref={editorRef}
          data-toutiao-editor
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onClick={handleEditorClick}
          onKeyDown={handleEditorKeyDown}
          className={`max-w-none px-8 py-8 text-[17px] leading-[1.8] text-[#1a1a1a] focus:outline-none sm:px-12 sm:py-10 ${
            fillHeight ? "min-h-full" : ""
          }`}
          style={fillHeight ? undefined : { minHeight: "min(520px, 55vh)" }}
        />

        {/* ── Image selection overlay ── */}
        {imgOverlay && selectedImgEl && (
          <div
            className="absolute z-20 pointer-events-none"
            style={{
              top: imgOverlay.top,
              left: imgOverlay.left,
              width: imgOverlay.width,
              height: imgOverlay.height,
            }}
          >
            {/* Red border */}
            <div className="absolute inset-0 border-2 border-red-500 rounded pointer-events-none" style={{ borderRadius: 6 }} />

            {/* Floating toolbar above */}
            <div className="absolute -top-9 left-0 flex items-center gap-0.5 bg-gray-800/95 rounded-lg px-1.5 py-1 pointer-events-auto shadow-lg">
              <span className="text-gray-400 text-[10px] px-1 whitespace-nowrap">宽度</span>
              {["25%", "50%", "75%", "100%"].map((w) => (
                <button
                  key={w}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); setImgWidth(w); }}
                  className="text-white text-[10px] px-1.5 py-0.5 rounded hover:bg-gray-600 cursor-pointer whitespace-nowrap transition-colors"
                >
                  {w}
                </button>
              ))}
              <span className="w-px h-3 bg-gray-600 mx-0.5 flex-shrink-0" />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); deleteSelectedImg(); }}
                className="flex items-center justify-center w-5 h-5 text-red-400 hover:text-red-300 cursor-pointer hover:bg-gray-600 rounded transition-colors"
                title="删除图片"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>

            {/* Move hint label */}
            <div className="absolute -top-9 right-0 flex items-center gap-1 text-[10px] text-gray-400 pointer-events-none whitespace-nowrap">
              <i className="ri-drag-move-line text-xs"></i>
              <span>拖动移位</span>
            </div>

            {/* Bottom-right resize handle */}
            <div
              className="absolute -bottom-2 -right-2 w-5 h-5 bg-red-500 rounded-sm cursor-se-resize pointer-events-auto flex items-center justify-center shadow"
              onMouseDown={handleResizeStart}
              title="拖动调整大小"
            >
              <i className="ri-corner-right-down-fill text-white" style={{ fontSize: 9 }}></i>
            </div>
          </div>
        )}

        {videoOverlay && selectedVideoWrapEl && (
          <div
            className="absolute z-20 pointer-events-none"
            style={{
              top: videoOverlay.top,
              left: videoOverlay.left,
              width: videoOverlay.width,
              height: videoOverlay.height,
            }}
          >
            <div className="absolute inset-0 rounded-xl border-2 border-red-500 pointer-events-none" />
            <div className="absolute -top-9 left-0 flex items-center gap-0.5 rounded-lg bg-gray-800/95 px-1.5 py-1 shadow-lg pointer-events-auto">
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  deleteSelectedVideoWrap();
                }}
                className="flex h-7 cursor-pointer items-center justify-center gap-1 rounded-md px-2 text-xs text-red-300 transition-colors hover:bg-gray-600 hover:text-red-200 whitespace-nowrap"
                title="删除视频/嵌入块"
              >
                <i className="ri-delete-bin-line text-sm" />
                删除视频块
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 py-2 text-xs text-gray-400">
        <span>
          已输入 <strong className="font-semibold text-gray-600">{wordCount}</strong> 字（不含空格）
        </span>
        <span className="hidden sm:inline">适当配图与分段，阅读体验更接近头条资讯流</span>
      </div>

      {/* Tip when image is selected */}
      {selectedImgEl && (
        <div className="flex flex-shrink-0 items-center gap-2 border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
          <i className="ri-information-line"></i>
          <span>已选中图片 · 上方浮层调宽度 · 拖右下角缩放</span>
        </div>
      )}
      {selectedVideoWrapEl && (
        <div className="flex flex-shrink-0 items-center gap-2 border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-600">
          <i className="ri-information-line"></i>
          <span>已选中视频/嵌入 · 点击上方「删除视频块」或按 Backspace / Delete</span>
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={imgFileRef} type="file" accept="image/*" className="hidden" onChange={handleLocalImageUpload} />
      <input ref={videoFileRef} type="file" accept="video/*" className="hidden" onChange={handleLocalVideoUpload} />

      {/* Image Dialog */}
      {showImageDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-800 text-sm">插入图片</h4>
              <button onClick={() => { setShowImageDialog(false); setImgForm({ url: "", alt: "", width: "100%" }); setImgTab("url"); }} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line"></i>
              </button>
            </div>

            {/* Tab */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
              {[{ value: "url", label: "图片链接" }, { value: "upload", label: "本地上传" }].map((t) => (
                <button key={t.value} onClick={() => { setImgTab(t.value as "url" | "upload"); setImgForm({ url: "", alt: "", width: "100%" }); }} className={`flex-1 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all whitespace-nowrap ${imgTab === t.value ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {imgTab === "url" ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">图片链接 *</label>
                  <input
                    type="url"
                    value={imgForm.url}
                    onChange={(e) => setImgForm((p) => ({ ...p, url: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                    autoFocus
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">选择图片文件 *</label>
                  <button
                    type="button"
                    onClick={() => imgFileRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 flex flex-col items-center gap-1.5 cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-all"
                  >
                    <i className="ri-upload-2-line text-2xl text-gray-300"></i>
                    <span className="text-xs text-gray-400">
                      {imgForm.url ? "已选择图片（点击重选）" : "点击选择图片文件"}
                    </span>
                    {imgForm.url && imgForm.url.startsWith("data:") && (
                      <span className="text-xs text-green-600 font-medium">已加载</span>
                    )}
                  </button>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">图片描述（alt）</label>
                <input
                  type="text"
                  value={imgForm.alt}
                  onChange={(e) => setImgForm((p) => ({ ...p, alt: e.target.value }))}
                  placeholder="图片说明文字"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">初始显示宽度</label>
                <div className="flex gap-2">
                  {["25%", "50%", "75%", "100%"].map((w) => (
                    <button key={w} type="button" onClick={() => setImgForm((p) => ({ ...p, width: w }))} className={`flex-1 py-2 text-xs rounded-xl border cursor-pointer whitespace-nowrap ${imgForm.width === w ? "bg-red-600 text-white border-red-600" : "border-gray-200 text-gray-600 hover:border-red-300"}`}>{w}</button>
                  ))}
                </div>
              </div>
              {imgForm.url && (
                <div className="rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center" style={{ height: 80 }}>
                  <img src={imgForm.url} alt="预览" className="max-h-full max-w-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowImageDialog(false); setImgForm({ url: "", alt: "", width: "100%" }); setImgTab("url"); }} className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleInsertImage} disabled={!imgForm.url.trim()} className="flex-1 py-2 text-sm text-white bg-red-600 rounded-xl cursor-pointer hover:bg-red-700 disabled:opacity-40 whitespace-nowrap">插入图片</button>
            </div>
          </div>
        </div>
      )}

      {/* Video Dialog */}
      {showVideoDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-800 text-sm">插入视频</h4>
              <button onClick={() => { setShowVideoDialog(false); setVideoForm({ url: "" }); setVideoTab("url"); setVideoUploading(false); }} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
              {[{ value: "url", label: "视频链接" }, { value: "upload", label: "本地上传" }].map((t) => (
                <button key={t.value} onClick={() => { setVideoTab(t.value as "url" | "upload"); setVideoForm({ url: "" }); setVideoUploading(false); }} className={`flex-1 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-all whitespace-nowrap ${videoTab === t.value ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {videoTab === "url" ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">视频链接 *</label>
                  <input type="url" value={videoForm.url} onChange={(e) => setVideoForm({ url: e.target.value })} placeholder="YouTube / Bilibili / 直链视频 URL" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" autoFocus />
                  <div className="bg-gray-50 rounded-xl px-3 py-2.5 mt-2 space-y-1 text-xs text-gray-400">
                    <p className="font-medium text-gray-600">支持的视频格式</p>
                    <p>• YouTube: https://www.youtube.com/watch?v=...</p>
                    <p>• Bilibili: https://www.bilibili.com/video/BV...</p>
                    <p>• 直链视频: https://example.com/video.mp4</p>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">选择视频文件 *</label>
                  <button
                    type="button"
                    onClick={() => videoFileRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 flex flex-col items-center gap-1.5 cursor-pointer hover:border-red-300 hover:bg-red-50/30 transition-all"
                  >
                    <i className="ri-video-upload-line text-2xl text-gray-300"></i>
                    <span className="text-xs text-gray-400">
                      {videoUploading ? "正在上传到对象存储…" : videoForm.url ? "已上传，可点击重选" : "点击选择视频文件"}
                    </span>
                    {videoForm.url && !videoUploading && (
                      <span className="text-xs text-green-600 font-medium">已就绪，可插入正文</span>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-1.5">支持 MP4、WebM，单文件不超过 200MB（存入租户 OSS）</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowVideoDialog(false); setVideoForm({ url: "" }); setVideoTab("url"); setVideoUploading(false); }} className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleInsertVideo} disabled={!videoForm.url.trim() || videoUploading} className="flex-1 py-2 text-sm text-white bg-red-600 rounded-xl cursor-pointer hover:bg-red-700 disabled:opacity-40 whitespace-nowrap">插入视频</button>
            </div>
          </div>
        </div>
      )}

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-xs p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-800 text-sm">插入链接</h4>
              <button onClick={() => { setShowLinkDialog(false); setLinkUrl(""); }} className="w-7 h-7 flex items-center justify-center text-gray-400 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">链接地址 *</label>
              <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400" autoFocus />
              <p className="text-xs text-gray-400 mt-1.5">选中文字后点击插入链接，可添加超链接</p>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowLinkDialog(false); setLinkUrl(""); }} className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={handleInsertLink} disabled={!linkUrl.trim()} className="flex-1 py-2 text-sm text-white bg-red-600 rounded-xl cursor-pointer hover:bg-red-700 disabled:opacity-40 whitespace-nowrap">插入</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** 预览用：去掉 script，降低误粘贴带来的风险（管理端仍视为可信内容）。 */
function stripScriptsFromHtml(html: string): string {
  if (typeof document === "undefined") return html;
  const d = document.createElement("div");
  d.innerHTML = html;
  d.querySelectorAll("script").forEach((n) => n.remove());
  return d.innerHTML;
}

const PHONE_PREVIEW_PROSE_CSS = `
  .article-phone-preview-body {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
    font-size: 16px;
    line-height: 1.75;
    color: #1a1a1a;
    word-break: break-word;
  }
  .article-phone-preview-body h2 { font-size: 1.125rem; font-weight: 700; margin: 1.1em 0 0.45em; line-height: 1.35; }
  .article-phone-preview-body h3 { font-size: 1rem; font-weight: 600; margin: 0.95em 0 0.35em; line-height: 1.4; }
  .article-phone-preview-body p { margin: 0.55em 0; }
  .article-phone-preview-body blockquote {
    margin: 0.85em 0; padding: 0.45em 0 0.45em 0.75em; border-left: 3px solid #f04142;
    background: #fafafa; color: #595959; font-size: 0.9em;
  }
  .article-phone-preview-body ul, .article-phone-preview-body ol { margin: 0.55em 0; padding-left: 1.2em; }
  .article-phone-preview-body li { margin: 0.2em 0; }
  .article-phone-preview-body a { color: #f04142; text-decoration: underline; }
  .article-phone-preview-body hr { border: none; border-top: 1px solid #eee; margin: 1.2em 0; }
  .article-phone-preview-body img { max-width: 100%; height: auto; border-radius: 4px; }
  .article-phone-preview-body .my-3, .article-phone-preview-body [style*="padding-bottom:56.25%"] { max-width: 100%; }
`;

function ArticlePhonePreview({
  title,
  summary,
  coverUrl,
  category,
  html,
}: {
  title: string;
  summary: string;
  coverUrl: string;
  category: string;
  html: string;
}) {
  const safeHtml = stripScriptsFromHtml(html);
  const plain = safeHtml.replace(/<[^>]+>/g, "").replace(/\s/g, "");
  const isEmpty = !title.trim() && !summary.trim() && !coverUrl && !plain;

  return (
    <div className="w-full max-w-[300px] mx-auto">
      <style>{PHONE_PREVIEW_PROSE_CSS}</style>
      <div className="rounded-[2rem] border-[10px] border-gray-900 bg-gray-900 shadow-2xl ring-1 ring-black/20">
        <div className="overflow-hidden rounded-[1.35rem] bg-white">
          {/* 状态栏 */}
          <div className="flex h-8 items-center justify-between border-b border-gray-100 bg-white px-3 pt-1">
            <span className="text-[11px] font-semibold tabular-nums text-gray-900">9:41</span>
            <div className="h-5 w-[72px] rounded-full bg-gray-900/90" aria-hidden />
            <div className="flex items-center gap-1 text-gray-900">
              <i className="ri-wifi-fill text-[11px]" />
              <i className="ri-battery-fill text-[11px]" />
            </div>
          </div>
          {category.trim() ? (
            <div className="border-b border-gray-50 px-3 py-1.5">
              <span className="inline-block rounded bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">{category}</span>
            </div>
          ) : null}

          <div
            className="max-h-[min(520px,62vh)] overflow-y-auto overscroll-y-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {coverUrl ? (
              <div className="aspect-[16/10] w-full bg-gray-100">
                <img src={coverUrl} alt="" className="h-full w-full object-cover object-center" />
              </div>
            ) : null}

            <div className="px-3 pb-6 pt-3">
              <h1 className="text-[1.15rem] font-bold leading-snug text-[#1a1a1a]">
                {title.trim() || <span className="text-gray-300">文章标题将显示在这里</span>}
              </h1>
              {summary.trim() ? (
                <p className="mt-2 text-xs leading-relaxed text-gray-500">{summary.trim()}</p>
              ) : null}

              {isEmpty ? (
                <p className="mt-6 text-center text-xs leading-relaxed text-gray-300">
                  正文将实时显示在此
                  <br />
                  <span className="text-[11px]">与会员端资讯详情相近</span>
                </p>
              ) : (
                <div
                  className="article-phone-preview-body mt-4"
                  dangerouslySetInnerHTML={{ __html: safeHtml || "" }}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a")) e.preventDefault();
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-center border-t border-gray-100 py-1.5">
            <div className="h-1 w-24 rounded-full bg-gray-300/90" aria-hidden />
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  /** 仅在服务端保存成功时调用；失败时不会调用 */
  onPublished: () => void | Promise<void>;
  editItem?: NewsItem | null;
  tenantLegacyId: string;
}

export default function NewsPublish({ onPublished, editItem, tenantLegacyId }: Props) {
  const isEditing = !!editItem;

  const [title, setTitle] = useState(editItem?.title ?? "");
  const [category, setCategory] = useState(editItem?.category ?? "协会公告");
  const [summary, setSummary] = useState(editItem?.summary ?? "");
  const [richContent, setRichContent] = useState(editItem?.content ?? "");
  const [coverUrl, setCoverUrl] = useState(editItem?.coverUrl ?? "");
  const [publishMode, setPublishMode] = useState<"immediate" | "scheduled">(() => {
    if (editItem?.status === "scheduled") return "scheduled";
    if (editItem?.scheduledAt?.trim()) return "scheduled";
    return "immediate";
  });
  const [scheduledDate, setScheduledDate] = useState(() => {
    if (editItem?.scheduledAt?.trim()) return scheduledIsoToLocalDateTime(editItem.scheduledAt).date;
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });
  const [scheduledTime, setScheduledTime] = useState(() => {
    if (editItem?.scheduledAt?.trim()) return scheduledIsoToLocalDateTime(editItem.scheduledAt).time;
    return "09:00";
  });
  const [success, setSuccess] = useState(false);
  const [successType, setSuccessType] = useState<"publish" | "scheduled">("publish");
  const coverFileRef = useRef<HTMLInputElement>(null);
  const [showCoverLibrary, setShowCoverLibrary] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const bodyVideoRefs = useMemo(() => extractBodyVideoRefsFromHtml(richContent), [richContent]);

  const canSubmit = title.trim() !== "" && richContent.replace(/<[^>]*>/g, "").trim() !== "";

  const handleCoverFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadTenantImageFile(file, tenantLegacyId);
      setCoverUrl(url);
    } catch (err) {
      reportError(err, "封面上传失败");
    }
    e.target.value = "";
  };

  const handleSubmit = () => {
    void (async () => {
      if (!canSubmit) return;

      const status: NewsItem["status"] = "published";
      let scheduledAt: string | null = null;
      if (publishMode === "scheduled") {
        const iso = localDateTimeToScheduledIso(scheduledDate, scheduledTime);
        if (!iso) {
          reportError(new Error("invalid-scheduled"), "定时时间无效，请重新选择");
          return;
        }
        scheduledAt = iso;
      }

      if (isEditing && editItem) {
        const publishDate = publishMode === "scheduled" ? scheduledDate : editItem.publishDate;
        try {
          const wxOpenId = getStoredWxOpenIdForTenant(tenantLegacyId)?.trim();
          const row = await apiPut<Record<string, unknown>>(`/api/tenants/${tenantLegacyId}/news/${encodeURIComponent(editItem.id)}`, {
            title,
            category,
            author: "超级管理员",
            status,
            publishDate,
            readCount: editItem.readCount,
            summary,
            content: richContent,
            coverUrl: coverUrl || null,
            scheduledAt,
            featured: false,
            ...(wxOpenId ? { wxOpenId } : {}),
          });
          newsStore.update(editItem.id, mapNews(row));
          setSuccessType(publishMode === "immediate" ? "publish" : "scheduled");
          setSuccess(true);
          setTimeout(() => {
            setSuccess(false);
            void onPublished();
          }, 1500);
        } catch (e) {
          reportError(e, "保存失败，请重试");
        }
        return;
      }

      const publishDate = publishMode === "scheduled" ? scheduledDate : new Date().toISOString().slice(0, 10);
      try {
        const wxOpenId = getStoredWxOpenIdForTenant(tenantLegacyId)?.trim();
        const row = await apiPost<Record<string, unknown>>(`/api/tenants/${tenantLegacyId}/news`, {
          title,
          category,
          author: "超级管理员",
          status,
          publishDate,
          readCount: 0,
          summary,
          content: richContent,
          coverUrl: coverUrl || null,
          scheduledAt,
          featured: false,
          ...(wxOpenId ? { wxOpenId } : {}),
        });
        newsStore.add(mapNews(row));
        setSuccessType(publishMode === "immediate" ? "publish" : "scheduled");
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          void onPublished();
        }, 1500);
      } catch (e) {
        reportError(e, "提交失败，请重试");
      }
    })();
  };

  const successMessages: Record<string, string> = {
    publish: isEditing ? "保存成功！" : "发布成功！",
    scheduled: "定时发布已设置",
  };

  return (
    <>
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-12 xl:min-h-[calc(100dvh-11rem)] xl:items-stretch">
      {/* 主栏：参考头条号「稿纸」式写作区 — 标题 + 摘要 + 正文一体 */}
      <div className="flex min-h-[min(420px,45vh)] flex-col xl:col-span-6 xl:min-h-0 xl:h-full">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
          <div className="flex-shrink-0 border-b border-gray-100 px-6 pb-2 pt-6 sm:px-10">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="请输入文章标题"
              className="w-full border-0 bg-transparent text-2xl font-bold leading-snug text-[#1a1a1a] placeholder:text-gray-300 focus:outline-none focus:ring-0 sm:text-[26px]"
            />
            <p className="mt-2 text-xs text-gray-400">
              标题会展示在资讯列表首位 · 已输入 <span className="font-medium text-gray-500">{title.length}</span> 字
            </p>
          </div>

          <div className="flex-shrink-0 border-b border-gray-100 px-6 py-4 sm:px-10">
            <p className="mb-2 text-xs font-medium text-gray-500">摘要（选填，列表副文案）</p>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="一句话概括正文亮点，帮助读者决定是否点开…"
              maxLength={100}
              rows={2}
              className="w-full resize-none rounded-xl border border-gray-100 bg-[#f7f8fa] px-4 py-3 text-sm leading-relaxed text-gray-800 placeholder:text-gray-400 focus:border-red-200 focus:outline-none focus:ring-1 focus:ring-red-100"
            />
            <p className="mt-1 text-right text-xs text-gray-400">{summary.length}/100</p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col px-0 pb-1 pt-2">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-6 sm:px-10">
              <p className="mb-0 text-xs font-medium text-gray-500">正文</p>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <i className="ri-eye-line text-sm" aria-hidden />
                预览
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col px-3 sm:px-5">
              <RichTextEditor
                key={editItem?.id ?? "new"}
                onChange={setRichContent}
                initialValue={editItem?.content}
                placeholder="从这里开始写正文。支持小标题、引用、配图与视频，工具栏可插入链接与多媒体。"
                storageTenantLegacyId={tenantLegacyId}
                fillHeight
              />
              {bodyVideoRefs.length > 0 ? (
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/90 px-3 py-2.5 sm:px-4">
                  <p className="mb-2 text-[11px] font-medium text-slate-600">
                    <i className="ri-links-line mr-1 text-slate-400" aria-hidden />
                    正文中的视频（每条为域名 + 完整链接）
                  </p>
                  <ul className="flex flex-col gap-2">
                    {bodyVideoRefs.map((v) => (
                      <li key={v.src}>
                        <div
                          className="inline-flex max-w-full flex-col items-start gap-1 rounded-lg border border-red-100 bg-white px-2.5 py-2 text-left shadow-sm ring-1 ring-red-50"
                          title={v.src}
                        >
                          <span className="text-[10px] font-semibold leading-none text-red-700">
                            域名 · {v.domain}
                          </span>
                          <span className="break-all font-mono text-[11px] leading-snug text-slate-800">
                            {v.src}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* 手机实时预览（大屏粘性） */}
      <div className="xl:col-span-3">
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gradient-to-b from-gray-50/80 to-gray-100/60 p-4 xl:sticky xl:top-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500">手机预览</h4>
            <span className="text-[10px] text-gray-400">实时同步</span>
          </div>
          <ArticlePhonePreview
            title={title}
            summary={summary}
            coverUrl={coverUrl}
            category={category}
            html={richContent}
          />
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-5 xl:col-span-3">
        {/* Publish Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h4 className="text-sm font-bold text-gray-900 mb-4">发布设置</h4>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">分类</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400 cursor-pointer">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-medium text-gray-500 mb-2">发布方式</label>
            <div className="space-y-2">
              {[
                {
                  value: "immediate",
                  label: "立即发布",
                  icon: "ri-send-plane-line",
                  desc: "保存后会员端立即可见",
                },
                {
                  value: "scheduled",
                  label: "定时发布",
                  icon: "ri-time-line",
                  desc: "保存后到达设定时间自动对会员可见",
                },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setPublishMode(opt.value as typeof publishMode)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-left ${
                    publishMode === opt.value ? "border-red-400 bg-red-50" : "border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${publishMode === opt.value ? "bg-red-500 text-white" : "bg-gray-100 text-gray-500"}`}>
                    <i className={`${opt.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold ${publishMode === opt.value ? "text-red-700" : "text-gray-700"}`}>{opt.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${publishMode === opt.value ? "border-red-500 bg-red-500" : "border-gray-300"}`}>
                    {publishMode === opt.value && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                  </div>
                </button>
              ))}
            </div>

            {publishMode === "scheduled" && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
                  <i className="ri-alarm-line"></i>设置发布时间
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-amber-700 mb-1">发布日期</label>
                    <input
                      type="date"
                      value={scheduledDate}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className="w-full border border-amber-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:border-amber-400 cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-amber-700 mb-1">发布时间</label>
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="w-full border border-amber-200 rounded-lg px-2 py-2 text-xs bg-white focus:outline-none focus:border-amber-400 cursor-pointer"
                    />
                  </div>
                </div>
                <p className="text-xs text-amber-600">
                  将于 <strong>{scheduledDate} {scheduledTime}</strong> 自动上线
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap cursor-pointer transition-all ${
              success
                ? "bg-green-600 text-white"
                : "bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            }`}
          >
            {success ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-check-line"></i>
                {successMessages[successType]}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-save-line"></i>
                保存
              </span>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center mt-2">
            {publishMode === "immediate"
              ? "保存后立即对会员端可见（无需审核）"
              : "保存后未到点不在会员端展示，到达设定时间自动上线"}
          </p>
        </div>

        {/* Cover Image */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h4 className="text-sm font-bold text-gray-900 mb-3">封面图片</h4>
          {coverUrl ? (
            <div className="relative rounded-xl overflow-hidden mb-3" style={{ height: 120 }}>
              <img src={coverUrl} alt="封面预览" className="w-full h-full object-cover object-top" />
              <button
                onClick={() => setCoverUrl("")}
                className="absolute top-2 right-2 w-6 h-6 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ) : (
            <div
              onClick={() => coverFileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl h-28 flex flex-col items-center justify-center cursor-pointer hover:border-red-300 transition-colors group mb-3"
            >
              <i className="ri-image-add-line text-2xl text-gray-300 group-hover:text-red-400 transition-colors mb-1"></i>
              <p className="text-xs text-gray-400">点击上传本地封面图</p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => coverFileRef.current?.click()}
              className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 text-xs rounded-xl cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-upload-2-line text-sm"></i>
              {coverUrl ? "重新上传" : "本地上传"}
            </button>
            <button
              type="button"
              onClick={() => setShowCoverLibrary(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-900 text-xs rounded-xl cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap border border-amber-200"
            >
              <i className="ri-image-2-line text-sm"></i>
              运营平台资源库
            </button>
            <input
              value={coverUrl.startsWith("data:") ? "" : coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="或输入图片链接..."
              className="flex-1 min-w-[140px] border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-red-400"
            />
          </div>
          <input ref={coverFileRef} type="file" accept="image/*" className="hidden" onChange={handleCoverFileUpload} />
        </div>

        {/* Tips */}
        <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5">
          <h4 className="text-xs font-bold text-gray-600 mb-3 flex items-center gap-1.5">
            <i className="ri-lightbulb-line text-amber-500"></i>编辑提示
          </h4>
          <ul className="space-y-1.5 text-xs text-gray-400">
            <li>• 工具栏：点击图片图标可上传本地图片或粘贴链接</li>
            <li>• 视频：支持上传本地视频 / YouTube / Bilibili</li>
            <li>• 选中文字后点击链接按钮添加超链接</li>
            <li>• 立即发布：保存后会员端立即可见；定时发布：按设定时刻自动可见</li>
            <li>• 中间栏实时预览；亦可点击正文旁「预览」全屏查看</li>
            <li>• 封面可选用运营平台「图片资源库」中的素材 URL</li>
          </ul>
        </div>
      </div>
    </div>
    {previewOpen && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-preview-dialog-title"
        onClick={() => setPreviewOpen(false)}
      >
        <div
          className="relative flex max-h-[92vh] w-full max-w-[min(100%,380px)] flex-col overflow-hidden rounded-2xl bg-gray-100 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
            <h2 id="article-preview-dialog-title" className="text-sm font-semibold text-gray-800">
              文章预览
            </h2>
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
              aria-label="关闭预览"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">
            <ArticlePhonePreview
              title={title}
              summary={summary}
              coverUrl={coverUrl}
              category={category}
              html={richContent}
            />
          </div>
          <p className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-2 text-center text-[11px] text-gray-400">
            与会员端资讯详情展示风格一致
          </p>
        </div>
      </div>
    )}
    {showCoverLibrary && (
      <ImageLibraryPicker
        tenantLegacyId={tenantLegacyId}
        defaultCategory="general"
        title="从运营平台图片资源库选择封面"
        onSelect={(img) => setCoverUrl(img.url)}
        onClose={() => setShowCoverLibrary(false)}
      />
    )}
    </>
  );
}
